import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { linkifyProductsReact } from '../utils/linkifyProducts';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ArticleReader() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authHeader } = useAuth();
  const [article, setArticle] = useState(null);
  const [progress, setProgress] = useState(0);
  const [bookmarkId, setBookmarkId] = useState(null);   // journal entry id if bookmarked
  const [bookmarking, setBookmarking] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/education/${id}`, { headers: authHeader() })
      .then(r => setArticle(r.data))
      .catch(() => navigate('/learn'));
    // Check whether this article is already bookmarked
    axios.get(`${API}/api/journal?kind=article`, { headers: authHeader() })
      .then(r => {
        const match = (r.data.entries || []).find(e => e.article_id === id);
        if (match) setBookmarkId(match.id);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const toggleBookmark = async () => {
    if (bookmarking || !article) return;
    setBookmarking(true);
    try {
      if (bookmarkId) {
        await axios.delete(`${API}/api/journal/${bookmarkId}`, { headers: authHeader() });
        setBookmarkId(null);
        toast.success('Removed from your Journal.');
      } else {
        const r = await axios.post(`${API}/api/journal`, {
          kind: 'article',
          article_id: id,
          title: article.title,
          content: (article.excerpt || article.title || '').slice(0, 800),
        }, { headers: authHeader() });
        if (r.data?.id) setBookmarkId(r.data.id);
        toast.success(r.data?.already ? 'Already in your Journal.' : 'Saved to your Journal · view in Profile.');
      }
    } catch {
      toast.error('Could not update bookmark. Please try again.');
    } finally {
      setBookmarking(false);
    }
  };

  useEffect(() => {
    const onScroll = () => {
      const el = containerRef.current;
      if (!el) return;
      const total = el.scrollHeight - el.clientHeight;
      if (total > 0) setProgress(Math.min(100, Math.max(0, (el.scrollTop / total) * 100)));
    };
    const el = containerRef.current;
    el?.addEventListener('scroll', onScroll);
    return () => el?.removeEventListener('scroll', onScroll);
  }, [article]);

  if (!article) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="gold-loader" />
      </div>
    );
  }

  // Split content into paragraphs for nicer typography
  const paragraphs = (article.content || '').split(/\n\n+/).filter(Boolean);

  return (
    <div
      ref={containerRef}
      className="reader-shell"
      data-testid="article-reader"
    >
      {/* Top progress bar */}
      <div className="reader-progress-track">
        <div className="reader-progress-fill" style={{ width: `${progress}%` }} />
      </div>

      {/* Top bar */}
      <div className="reader-topbar">
        <button
          onClick={() => navigate(-1)}
          className="btn-ghost"
          data-testid="reader-back"
          style={{ padding: '6px 8px', fontSize: '0.93rem' }}
        >
          <i className="fa-solid fa-arrow-left" /> <span style={{ marginLeft: 4 }}>Library</span>
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: '0.73rem', color: 'var(--text-dim)', letterSpacing: '0.16em', textTransform: 'uppercase' }}>
            {Math.round(progress)}% read
          </div>
          <button
            onClick={toggleBookmark}
            disabled={bookmarking}
            data-testid="reader-bookmark"
            title={bookmarkId ? 'Remove from Journal' : 'Save to Journal'}
            style={{
              background: 'none',
              border: '1px solid var(--border-soft)',
              color: bookmarkId ? 'var(--gold)' : 'var(--text-dim)',
              width: 30, height: 30,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: bookmarking ? 'wait' : 'pointer',
              fontSize: '0.87rem',
              transition: 'color 0.2s',
            }}
          >
            <i className={`fa-${bookmarkId ? 'solid' : 'regular'} fa-bookmark`} />
          </button>
        </div>
      </div>

      {/* Article header */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="reader-header"
      >
        <div style={{ fontSize: '0.73rem', letterSpacing: '0.22em', color: 'var(--gold)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 12 }}>
          {article.category}
        </div>
        <h1 className="reader-title">{article.title}</h1>
        <div className="reader-meta">
          <span><i className="fa-solid fa-user-doctor" style={{ marginRight: 6, color: 'var(--gold)' }} />Dr. Vikram Dukle</span>
          <span style={{ margin: '0 8px', color: 'var(--text-dim)' }}>·</span>
          <span><i className="fa-regular fa-clock" style={{ marginRight: 6 }} />{article.read_time}</span>
        </div>
      </motion.div>

      {/* Lead/excerpt — drop cap style */}
      {article.excerpt && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="reader-lead"
        >
          {linkifyProductsReact(article.excerpt, 'lead')}
        </motion.p>
      )}

      {/* Body */}
      <div className="reader-body">
        {paragraphs.map((p, i) => (
          <motion.p
            key={i}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: Math.min(i, 6) * 0.04 }}
          >
            {linkifyProductsReact(p, `body-${i}`)}
          </motion.p>
        ))}
      </div>

      {/* Tags */}
      {article.tags && article.tags.length > 0 && (
        <div className="reader-tags">
          {article.tags.map(t => (
            <span key={t} className="reader-tag">{t}</span>
          ))}
        </div>
      )}

      {/* Editorial signature */}
      <div className="reader-signature">
        <div className="reader-signature-eyebrow">— Dr. Vikram Dukle, Founder</div>
        <div style={{ fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic', fontSize: '1.1rem', color: 'var(--text-muted)', lineHeight: 1.7 }}>
          "Because every touch, thought, and truth deserves Intimacy."
        </div>
      </div>

      {/* CTAs */}
      <div className="reader-cta-row">
        <button
          onClick={() => navigate('/consult')}
          className="btn-gold"
          data-testid="reader-book-cta"
          style={{ flex: 1, fontSize: '0.79rem' }}
        >
          <i className="fa-brands fa-whatsapp" /> BOOK CONSULT
        </button>
        <button
          onClick={() => navigate('/chat')}
          className="btn-outline"
          data-testid="reader-vivi-cta"
          style={{ flex: 1, fontSize: '0.79rem' }}
        >
          <i className="fa-solid fa-comment-dots" /> ASK VIVI
        </button>
      </div>

      {/* WhatsApp share */}
      <div style={{ padding: '0 16px 16px' }}>
        <a
          href={`https://wa.me/?text=${encodeURIComponent(`${article.title}\n\n${article.excerpt || ''}\n\nRead more on INTIVIV — https://www.intiviv.com/article/${article.id}`)}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="reader-share-whatsapp"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            width: '100%', padding: '13px',
            background: 'linear-gradient(135deg, #25D366, #128C7E)',
            color: 'white',
            fontSize: '0.81rem', fontWeight: 700, letterSpacing: '0.16em',
            textTransform: 'uppercase', textDecoration: 'none',
            fontFamily: 'var(--font-ui)',
            boxShadow: '0 12px 28px -14px rgba(37,211,102,0.6)',
          }}
        >
          <i className="fa-brands fa-whatsapp" style={{ fontSize: '1.26rem' }} />
          SHARE ON WHATSAPP
        </a>
      </div>

      <div style={{ height: 'calc(var(--bottom-h) + 24px)' }} />
    </div>
  );
}
